import type { ReactNode } from 'react'
import type { DetailSession } from './detail-session'
import type { ParticipantStatus } from './participant-state'
import type { SessionParticipant, SessionResultsData } from '~/gql/sessions/sessions'
import type { SurveyResults } from '~/gql/surveys/surveys'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { participantsInState } from './participant-state'
import { participantsCsv } from './session-participants-csv'
import SessionExports from './SessionExports'
import SessionHeader from './SessionHeader'
import SessionInfoCard from './SessionInfoCard'
import SessionKpis from './SessionKpis'
import SessionParticipantsCard from './SessionParticipantsCard'
import SessionPathCard from './SessionPathCard'
import SessionSurveyCard from './SessionSurveyCard'
import SessionTrainersCard from './SessionTrainersCard'

type IProps = {
  session: DetailSession
  /** Résultats de la session ; absents pour qui ne voit pas les statistiques. */
  results?: SessionResultsData
  timeZone?: string | null
  templateName?: string
  /** Actions de l'en-tête (« Modifier »). */
  headerActions?: ReactNode
  /** Tient la place des résultats attendus mais pas encore là (chargement, échec). */
  resultsPlaceholder?: ReactNode
  onDownloadReport: () => void
  downloadingReport: boolean
  /** Action de chaque participant (abandon, #324), pour qui peut gérer la session. */
  participantAction?: (participant: SessionParticipant) => ReactNode
  /** Ce que les apprenants ont dit du parcours (#320), pour qui voit les statistiques. */
  survey?: SurveyResults
  /** Les relevés d'assiduité de tous les inscrits (#322), pour l'admin. */
  attendance?: ReactNode
}

/**
 * La fiche d'une session, en lecture (#243) : sa configuration, ses formateurs (#244), son parcours,
 * ses participants et ses résultats. Vue de présentation : la page lui passe
 * ses données, la démonstration du centre d'aide des données fictives.
 */
function SessionDetailView({ session, results, timeZone, templateName, headerActions, resultsPlaceholder, onDownloadReport, downloadingReport, participantAction, survey, attendance }: IProps) {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState<ParticipantStatus | ''>('')
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, timeZone, DATE_WITH_YEAR)
  const visible = participantsInState(results?.participants ?? [], state)

  const exportCsv = () => {
    const { headers, rows } = participantsCsv(visible, t, formatDate)
    downloadCSV(session.name, headers, rows)
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <SessionHeader session={session} timeZone={timeZone} actions={headerActions} />
      {results ? <SessionKpis stats={results.stats} participants={results.participants} /> : resultsPlaceholder}
      {survey && <SessionSurveyCard results={survey} formatDate={formatDate} />}
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="flex flex-col gap-6">
          <SessionInfoCard session={session} timeZone={timeZone} templateName={templateName} />
          <SessionTrainersCard trainers={session.trainers} />
        </div>
        <SessionPathCard session={session} results={results} timeZone={timeZone} />
      </div>
      {results && (
        <SessionParticipantsCard
          participants={visible}
          state={state}
          onStateChange={setState}
          formatDate={formatDate}
          renderAction={participantAction}
          exports={(
            <SessionExports
              onCsv={exportCsv}
              onPdf={onDownloadReport}
              pdfLoading={downloadingReport}
              questionsHref={`/back-office/questions?sessionId=${session.id}`}
              attendance={attendance}
            />
          )}
        />
      )}
    </div>
  )
}

export default SessionDetailView
